/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { NewsRequestUtilService } from '../../utils/news-utils.service';
import { DataApiService } from './data-api-service';
import { map } from 'rxjs/operators';

interface StoriesResponse {
  stories: Array<{
    main_article: {
      publish_time: string;
      title: string;
      id: string;
      sentiment: string;
      teaser: string;
      // Add other fields as needed
    };
    story_id: string;
    story_size: number;
    date_from: string;
    date_to: string;
    news_ids: string[];
  }>;
  used_cache: boolean;
  cache_key: string;
}
@Injectable({
  providedIn: 'root',
})
export class StoriesService {
  constructor(
    private apiService: DataApiService,
    private newsRequestUtil: NewsRequestUtilService,
  ) {}

  getStoryById(storyIds: string | string[], params: any) {
    const normalizedStoryIds = Array.isArray(storyIds) ? storyIds : [storyIds];
    const requestBody = this.newsRequestUtil.createStoriesRequest(
      {
        ...params.customParams,
        story_ids: normalizedStoryIds,
      },
      params.body,
    );

    return this.apiService
      .post<StoriesResponse>('stories', requestBody, {}, 'v4')
      .pipe(
        map((respArg) => {
          const resp = JSON.parse(JSON.stringify(respArg));
          if (Array.isArray(resp?.articles)) {
            resp.articles.forEach((article) => this.normalizeArticle(article));
          }
          if (resp?.main_article) {
            this.normalizeArticle(resp.main_article);
          }
          return resp;
        }),
      );
  }

  getStories(params: any) {
    const requestBody = this.newsRequestUtil.createStoriesRequest(
      params.customParams,
      params.body,
    );

    return this.apiService
      .post<StoriesResponse>('stories', requestBody, {}, 'v4')
      .pipe(
        map((responseArg) => {
          const resp = JSON.parse(JSON.stringify(responseArg));
          resp?.stories?.forEach((story) =>
            this.normalizeArticle(story?.main_article),
          );
          return resp;
        }),
      );
  }

  private normalizeArticle(article: any): void {
    if (!article) {
      return;
    }

    this.normalizeEvents(article);
    this.normalizeMatches(article);
  }

  private normalizeEvents(article: any): void {
    if (!Array.isArray(article?.events)) {
      return;
    }

    article.events.forEach((event) => {
      if (!event?.span) {
        return;
      }

      const spanContext = this.getSpanContext(event.span, article.locations);
      if (!spanContext) {
        return;
      }

      event.where = spanContext.location;
      event.span.start -= spanContext.offset;
      event.span.end -= spanContext.offset;
    });
  }

  private normalizeMatches(article: any): void {
    if (!Array.isArray(article?.matches)) {
      return;
    }

    article.matches.forEach((match) => {
      if (!Array.isArray(match?.spans)) {
        return;
      }

      match.spans.forEach((span) => {
        const spanContext = this.getSpanContext(span, article.locations);
        if (!spanContext) {
          return;
        }

        span.location = spanContext.location;
        span.start -= spanContext.offset;
        span.end -= spanContext.offset;
      });
    });
  }

  private getSpanContext(
    span: { start: number; end: number },
    locations: any,
  ): { location: 'title' | 'content'; offset: number } | null {
    if (
      !span ||
      typeof span.start !== 'number' ||
      typeof span.end !== 'number' ||
      !locations
    ) {
      return null;
    }

    const titleLocation = locations?.title;
    if (
      titleLocation &&
      typeof titleLocation.end === 'number' &&
      span.end <= titleLocation.end &&
      typeof titleLocation.start === 'number'
    ) {
      return {
        location: 'title',
        offset: titleLocation.start,
      };
    }

    const contentLocation = locations?.content;
    if (contentLocation && typeof contentLocation.start === 'number') {
      return {
        location: 'content',
        offset: contentLocation.start,
      };
    }

    return null;
  }
}
